/**
 * TEMU「平台 JSON」控制台日志工具
 * ================================
 *
 * 目的：凡是「通过同步更新去打 Temu 平台接口取数据」的页面（产品信息页、店铺信息页、
 * 库存 / 营销 / 退货单 / 议价单 …），都能在浏览器控制台直接看到 **平台返回的真实 JSON**
 * （后端未改写的原始响应，含 success / errorCode / result）。
 *
 * 数据来源：后端 `TemuSyncPlatformDumpService` 在每次同步时落盘的 dump，
 * 通过 `GET /temu/shops/{shopId}/sync-platform-dump?kind=xxx` 读回。
 * dump 结构大致为：
 *   { shopId, kind, api, startedAt, finishedAt, callCount,
 *     calls: [ { at, api, request, response } ] }
 * 其中 `calls[].response` 就是平台的原始返回。
 *
 * 控制台用法（浏览器 DevTools Console）：
 *   ipeTemuJson.help()                  查看帮助
 *   ipeTemuJson.dump('shop-profile')    打印当前店铺最近一次「店铺信息」同步的平台原始 JSON
 *   ipeTemuJson.dump('product')         商品
 *   ipeTemuJson.dump('product', 123)    指定 shopId
 *   ipeTemuJson.list()                  列出所有支持的 kind
 *   ipeTemuJson.copy('product')         复制到剪贴板
 *   ipeTemuJson.enable() / disable()    开关「同步完成后自动打印」
 *
 * 自动打印：默认开启。每个同步任务跑完后会自动在控制台折叠输出该 kind 的平台 JSON；
 * 若该 kind 后端没有落盘 dump（例如证书 / 模板复制类），会静默跳过。
 */
import { fixedUi } from '@/i18n/fixedUi';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';

const ENABLED_STORAGE_KEY = 'ipe_temu_platform_json_log';
const LOG_TAG = '[TEMU平台JSON]';
const GROUP_STYLE = 'color:#e8590c;font-weight:700';

/** 后端 TemuSyncPlatformDumpService 支持的全部 kind（含中文说明） */
export const TEMU_PLATFORM_JSON_KINDS = [
  { kind: 'product', get label() { return fixedUi("商品"); }, api: 'bg.glo.goods.list.get（PA 网关；旧 bg.goods.list.get 已随 2025-09 迁移下线，打 CN 会回 7000016 type not exists）' },
  { kind: 'shop-profile', get label() { return fixedUi("店铺信息"); }, api: '店铺信息接口组（授权后与「同步更新」共用）：bg.mall.info.get（CN）/ bg.open.accesstoken.info.get.global（PA）+ 尺码表 bg.glo.goods.sizecharts.get（PA，result.sizeSpecDataList[].supplierId 即商家 ID）+ 一级类目 + 品牌；尺码表没带出 ID 时再附包装清单待办链路' },
  { kind: 'pricing-review', label: '成本议价单 / 建议零售价审核', api: 'bg.goods.price.review.*' },
  { kind: 'pricing-adjust', get label() { return fixedUi("建议零售价"); }, api: 'rrp / skc 相关接口' },
  { kind: 'stock', label: '仓库库存', api: 'bg.skustock.*' },
  { kind: 'marketing', label: '营销活动', api: 'bg.marketing.activity.list.get.global' },
  { kind: 'marketing-product', label: '营销报名记录', api: '营销商品 / 报名相关接口' },
  { kind: 'sales', label: '销量', api: 'bg.goods.salesv2.get' },
  { kind: 'fulfillment', label: '履约 / 发货', api: 'bg.shiporder.staging.get' },
  { kind: 'print', label: '打印 / 箱唛', api: 'bg.logistics.boxmarkinfo.get' },
  { kind: 'aftersale', label: '售后 / 样品', api: 'bg.sample.order.get' },
  { kind: 'return-orders', label: '退货单', api: '退货包裹相关接口' },
  { kind: 'quality', label: '质检', api: '质检相关接口' },
  { kind: 'packing', label: '包装清单', api: 'bg.themis.query.new.suit.property.list（PA 网关；打 CN 会回 7000016 type not exists）' },
  { kind: 'ads', label: '广告', api: 'bg.glo.searchrec.ad.detail.query' },
  { kind: 'freight-templates', get label() { return fixedUi("运费模板"); }, api: 'bg.glo.logistics.template.get（按站点并行；看 calls[].request.siteIds / response.result.freightTemplates）' },
  { kind: 'warehouses', label: '发货仓', api: 'bg.logistics.warehouse.list.get / 站点仓列表' },
  { kind: 'compliance', label: '合规信息 / 实拍图', api: '拉取后台：bg.compliance.metadata.get + bg.goods.compliancelabel.get（并行）；实拍提交：bg.flash.open.upload.recognize（ruleStatus 1–7）' },
  { kind: 'certificate', label: '产品证书资质', api: '① cert.query 拉待上传证书类型 → ② queryNeedUploadItems 查填写规则 → ③ uploadProductCert 提交' },
];

/**
 * 同步任务 kind（TemuSyncJobContext 里的 TEMU_SYNC_KINDS）→ dump kind。
 * 没有映射的同步类型（证书、模板复制、自动绑定…）后端不会落盘平台 JSON，将被跳过。
 */
export const SYNC_KIND_TO_DUMP_KIND = {
  product: 'product',
  shopProfile: 'shop-profile',
  stock: 'stock',
  marketing: 'marketing',
  marketingProduct: 'marketing-product',
  returnOrders: 'return-orders',
  discuss: 'pricing-review',
  rrpAudit: 'pricing-review',
  rrpSkc: 'pricing-adjust',
  freightTemplates: 'freight-templates',
  warehouses: 'warehouses',
  compliance: 'compliance',
  complianceAudit: 'certificate',
  certificate: 'certificate',
};

/** 同步 kind → dump kind；未映射返回 '' */
export function dumpKindForSyncKind(syncKind) {
  return SYNC_KIND_TO_DUMP_KIND[String(syncKind || '').trim()] || '';
}

function kindLabel(kind) {
  const found = TEMU_PLATFORM_JSON_KINDS.find((item) => item.kind === kind);
  return found ? found.label : kind;
}

/** 自动打印开关（默认开启，可用 localStorage 覆盖） */
export function isTemuPlatformJsonLogEnabled() {
  if (typeof localStorage === 'undefined') return true;
  try {
    const raw = localStorage.getItem(ENABLED_STORAGE_KEY);
    if (raw == null) return true;
    return raw !== '0' && raw !== 'false';
  } catch {
    return true;
  }
}

export function setTemuPlatformJsonLogEnabled(enabled) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(ENABLED_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** 汇总 dump 里每个平台接口的 api / 返回是否成功，方便一眼扫出是哪个接口出的问题 */
export function summarizeDumpCalls(dump) {
  const calls = Array.isArray(dump?.calls) ? dump.calls : [];
  return calls.map((call, index) => {
    const response = call?.response;
    return {
      '#': index + 1,
      api: call?.api || '',
      at: call?.at || '',
      success: response?.success,
      errorCode: response?.errorCode,
      msg: response?.errorMsg || response?.msg || '',
      pageNum: call?.pageNum,
      dataSize: Array.isArray(response?.result?.data)
        ? response.result.data.length
        : (Array.isArray(response?.result?.list) ? response.result.list.length : undefined),
    };
  });
}

/**
 * 直接把一段平台 JSON 打到控制台（折叠分组，原始对象，可在 DevTools 里展开）。
 * 返回传入的 json，方便链式使用。
 */
export function logTemuPlatformJson(kind, json, meta = {}) {
  const { shopId, label, reason, apiHint } = meta;
  const name = label || kindLabel(kind);
  const title = `${LOG_TAG} ${name}（${kind}）${shopId ? ` · shop ${shopId}` : ''}${reason ? ` · ${reason}` : ''}`;

  if (typeof console === 'undefined') return json;
  console.groupCollapsed(`%c${title}%c`, GROUP_STYLE, '');
  try {
    if (apiHint) {
      console.log('接口：', apiHint);
    }
    if (json?.startedAt) {
      console.log('同步时间：', json.startedAt, '→', json.finishedAt);
    }
    if (json?.callCount != null) {
      console.log('平台调用次数：', json.callCount, '（本次 dump 保留', json.retainedCalls ?? '?', '条）');
    }
    if (Array.isArray(json?.calls) && json.calls.length) {
      const rows = summarizeDumpCalls(json);
      console.log('平台接口一览：');
      if (console.table) console.table(rows);
    }
    console.log('平台原始 JSON（未改写）：', json);
  } catch {
    /* ignore */
  } finally {
    if (console.groupEnd) console.groupEnd();
  }
  return json;
}

/**
 * 读取后端落盘的最新 dump 并打印。用于「同步完成后自动打印」以及各页面的手动调用。
 * 永不抛错：没有 dump / 网络失败时静默返回 null。
 */
export async function logTemuSyncPlatformDump(shopId, kind, meta = {}) {
  const id = String(shopId ?? '').trim();
  const dumpKind = String(kind || '').trim();
  if (!id || !dumpKind) return null;
  if (!isTemuPlatformJsonLogEnabled() && !meta.force) return null;
  try {
    const json = await temuPlatformAPI.fetchSyncPlatformDump(id, dumpKind);
    return logTemuPlatformJson(dumpKind, json, { ...meta, shopId: id });
  } catch {
    // 该 kind 没有落盘 dump（如证书/模板类）或后端不可达时，不打扰用户
    return null;
  }
}

/** 同步任务完成后按 kind 映射自动打印；未映射的同步类型直接跳过 */
export function logTemuSyncPlatformDumpForSyncKind(shopId, syncKind, meta = {}) {
  if (!isTemuPlatformJsonLogEnabled()) return null;
  const dumpKind = dumpKindForSyncKind(syncKind);
  if (!dumpKind) return null;
  return logTemuSyncPlatformDump(shopId, dumpKind, meta);
}

async function copyDumpToClipboard(shopId, kind) {
  const json = await temuPlatformAPI.fetchSyncPlatformDump(shopId, kind);
  const text = JSON.stringify(json, null, 2);
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return text.length;
  }
  return 0;
}

/**
 * 挂载 `window.ipeTemuJson`，让排查人员可以在控制台随时拉取任意 kind 的平台 JSON。
 * @param {{ getShopId?: () => string }} options
 */
export function installTemuPlatformJsonConsole(options = {}) {
  if (typeof window === 'undefined') return null;
  const getShopId = typeof options.getShopId === 'function' ? options.getShopId : () => '';
  const api = {
    /** 查看帮助 */
    help() {
      console.log(
        `%c${LOG_TAG} 控制台排查工具%c

  ipeTemuJson.dump(kind, [shopId])    打印某个 kind 的平台原始 JSON
  ipeTemuJson.copy(kind, [shopId])    复制某个 kind 的平台原始 JSON 到剪贴板
  ipeTemuJson.list()                  列出所有 kind
  ipeTemuJson.enable() / disable()    开关「同步完成后自动打印」
  ipeTemuJson.status()                查看当前状态

当前店铺 shopId：${getShopId() || '(未选中)'}
可用 kind：${TEMU_PLATFORM_JSON_KINDS.map((k) => k.kind).join(' / ')}`,
        GROUP_STYLE,
        '',
      );
      return TEMU_PLATFORM_JSON_KINDS;
    },
    list() {
      if (typeof console !== 'undefined' && console.table) console.table(TEMU_PLATFORM_JSON_KINDS);
      return TEMU_PLATFORM_JSON_KINDS;
    },
    async dump(kind, shopId) {
      const id = String(shopId ?? getShopId() ?? '').trim();
      return logTemuSyncPlatformDump(id, kind, { force: true, reason: '手动' });
    },
    async copy(kind, shopId) {
      const id = String(shopId ?? getShopId() ?? '').trim();
      return copyDumpToClipboard(id, kind);
    },
    enable() {
      setTemuPlatformJsonLogEnabled(true);
      return true;
    },
    disable() {
      setTemuPlatformJsonLogEnabled(false);
      return false;
    },
    status() {
      const enabled = isTemuPlatformJsonLogEnabled();
      if (typeof console !== 'undefined') {
        console.log(`${LOG_TAG} 自动打印：${enabled ? '开启' : fixedUi("关闭")}；当前 shopId：${getShopId() || '(未选中)'}`);
      }
      return { enabled, shopId: getShopId() };
    },
  };

  window.ipeTemuJson = api;
  announceOnce();
  return api;
}

let announced = false;

/** 每个页面会话只提示一次控制台入口，避免刷屏 */
function announceOnce() {
  if (announced) return;
  announced = true;
  try {
    console.log(
      `%c${LOG_TAG}%c 已开启平台 JSON 控制台日志；同步完成后会自动打印平台原始返回。
在控制台执行 %cipeTemuJson.help()%c 查看可用命令（如 ipeTemuJson.dump('shop-profile')）。`,
      GROUP_STYLE,
      '',
      'color:#1971c2;font-weight:700',
      '',
    );
  } catch {
    /* ignore */
  }
}
